"use client"

import { useEffect, useState } from "react"
import { Link } from "react-router";
import { useAuth } from "../../contexts/authContext"
import { getUserQuizSession } from "../../services/quizSessionService"
import {
  Award,
} from "lucide-react"
import ProfileStatsCard from "./components/ProfileStatsCard"
import QuizHistoryTab from "./components/QuizHistoryTab"
import CoursesTab from "./components/CoursesTab"

function ProfilePage() {
  const { user } = useAuth()
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState("history")
  const [stats, setStats] = useState({
    totalQuizzes: 0,
    completedQuizzes: 0,
    averageScore: 0,
    bestScore: 0,
    completionRate: 0,
    totalCourses: 0,
  })

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const data = await getUserQuizSession()
        setSessions(data)

        // Calculate stats if there are sessions
        if (data.length > 0) {
          // Get completed sessions only
          const completedSessions = data.filter((session) => session.completed_at !== null)
          const totalQuizzes = data.length
          const completedQuizzes = completedSessions.length

          // Calculate scores for completed quizzes
          const scores = completedSessions.map((session) =>
            session.total_question_count > 0 ? (session.correct_answer_count / session.total_question_count) * 100 : 0,
          )

          const averageScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0

          const bestScore = scores.length > 0 ? Math.max(...scores) : 0
          const completionRate = (completedQuizzes / totalQuizzes) * 100

          // Get unique courses
          const uniqueCourses = new Set(data.map((session) => session.topic_detail?.course_detail?.id).filter(Boolean))

          setStats({
            totalQuizzes,
            completedQuizzes,
            averageScore,
            bestScore,
            completionRate,
            totalCourses: uniqueCourses.size,
          })
        }
      } catch (error) {
        console.error("Error fetching sessions:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchSessions()
  }, [])

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4">
          <div className="skeleton h-12 w-3/4"></div>
          <div className="skeleton h-[200px] w-full rounded-xl"></div>
          <div className="skeleton h-[400px] w-full rounded-xl"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-bold mb-8">Dashboard</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left column - User profile */}
        <div className="lg:col-span-1 relative">
          <ProfileStatsCard user={user} stats={stats} className={'sticky top-2'} />
        </div>

        {/* Right column - Quiz history and other tabs */}
        <div className="lg:col-span-2">
          {/* Learning Journey Banner */}
         <div className="w-full flex items-center justify-between bg-primary/10 border border-primary/20 rounded-lg px-6 py-4 mb-6">
            <div>
              <h2 className="text-xl font-semibold text-primary mb-1">Your Learning Journey</h2>
              <p className="text-sm text-primary/80">
                Track your quiz progress, achievements, and courses as you grow!
              </p>
            </div>
            <Link
              to="/discover"
              className="btn btn-primary btn-sm"
            >
              Discover Quizzes
            </Link>
          </div>
          <div className="tabs tabs-boxed mb-6">
            <a className={`tab ${activeTab === "history" ? "tab-active" : ""}`} onClick={() => setActiveTab("history")}>
              Quiz History
            </a>
            <a
              className={`tab ${activeTab === "achievements" ? "tab-active" : ""}`}
              onClick={() => setActiveTab("achievements")}
            >
              Achievements
            </a>
            <a className={`tab ${activeTab === "courses" ? "tab-active" : ""}`} onClick={() => setActiveTab("courses")}>
              Courses
            </a>
          </div>

          {activeTab === "history" && (
            <QuizHistoryTab 
              sessions={sessions}
              loading={loading}
            />
          )}

          {activeTab === "achievements" && (
            <div className="card bg-base-100 shadow-xl">
              <div className="card-body">
                <h2 className="card-title flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  Achievements
                </h2>
                <p className="text-sm opacity-70 -mt-1 mb-4">Track your learning milestones and achievements</p>

                <div className="text-center py-12 border rounded-lg bg-base-200">
                  <Award className="h-12 w-12 mx-auto opacity-50 mb-4" />
                  <h3 className="text-lg font-medium mb-1">No achievements yet</h3>
                  <p className="text-sm opacity-70">Complete quizzes to earn achievements and badges</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === "courses" && (
           <CoursesTab sessions={sessions} loading={loading} />
          )}
        </div>
      </div>
    </div>
  )
}

export default ProfilePage
